// Level Collection - All levels for the game

import {LevelConfig, Fragment, generateFragmentPairHints} from './levelSystem';
import { bachInvention784Level } from './bachInvention13Level';

// Bach Invention No. 1 in C Major, BWV 772 - 8 beats total
const bach772Fragments: Fragment[] = [
  {
    id: 'fragment_772_1',
    name: 'Fragment 1',
    voice: 'right',
    beatDuration: 4.25,     // Extends slightly beyond 4 beats
    notes: [
      {note: 'C4', time: "0:0:1", duration: "16n"},
      {note: 'D4', time: "0:0.5", duration: "16n"},
      {note: 'E4', time: "0:0.75", duration: "16n"},
      {note: 'F4', time: "0:1:0", duration: "16n"},
      {note: 'D4', time: "0:1:1", duration: "16n"},
      {note: 'E4', time: "0:1:2", duration: "16n"},
      {note: 'C4', time: "0:1:3", duration: "16n"},
      {note: 'G4', time: "0:2:0", duration: "8n"},
      {note: 'C5', time: "0:2:2", duration: "8n"},
      {note: 'B4', time: "0:3:0", duration: "8n"},
      {note: 'C5', time: "0:3:2", duration: "8n"},
      {note: 'D5', time: "0:4:0", duration: "16n"},
    ],
    correctPosition: {
      beat: 0,    // Beat 0 - timeline start
      pitch: 0    // C5 octave
    }
  },
  {
    id: 'fragment_772_2',
    name: 'Fragment 2',
    voice: 'right',
    beatDuration: 4,        // 4 beats duration
    notes: [
      {note: 'G4', time: "0:0:1", duration: "16n"},
      {note: 'A4', time: "0:0:2", duration: "16n"},
      {note: 'B4', time: "0:0:3", duration: "16n"},
      {note: 'C5', time: "0:1:0", duration: "16n"},
      {note: 'A4', time: "0:1:1", duration: "16n"},
      {note: 'B4', time: "0:1:2", duration: "16n"},
      {note: 'G4', time: "0:1:3", duration: "16n"},
      {note: 'D5', time: "0:2:0", duration: "8n"},
      {note: 'G5', time: "0:2:2", duration: "8n"},
      {note: 'F#5', time: "0:3:0", duration: "8n"},
      {note: 'G5', time: "0:3:2", duration: "8n"},
    ],
    correctPosition: {
      beat: 4,    // Beat 4 - timeline middle
      pitch: 0    // C5 octave
    }
  },
  {
    id: 'fragment_772_3',
    name: 'Fragment 3',
    voice: 'left',
    beatDuration: 2.5,      // 2.5 beats
    notes: [
      {note: 'C3', time: "0:0:1", duration: "16n"},
      {note: 'D3', time: "0:0:2", duration: "16n"},
      {note: 'E3', time: "0:0:3", duration: "16n"},
      {note: 'F3', time: "0:1:0", duration: "16n"},
      {note: 'D3', time: "0:1:1", duration: "16n"},
      {note: 'E3', time: "0:1:2", duration: "16n"},
      {note: 'C3', time: "0:1:3", duration: "16n"},
      {note: 'G3', time: "0:2:0", duration: "8n"},
      {note: 'G2', time: "0:2:2", duration: "8n"},
    ],
    correctPosition: {
      beat: 2,    // Beat 2 - early timeline
      pitch: 0  // C4 octave (one octave below right hand)
    }
  },
  {
    id: 'fragment_772_4',
    name: 'Fragment 4',
    voice: 'left',
    beatDuration: 2,        // 2 beats
    notes: [
      {note: 'G3', time: "0:0:1", duration: "16n"},
      {note: 'A3', time: "0:0:2", duration: "16n"},
      {note: 'B3', time: "0:0:3", duration: "16n"},
      {note: 'C4', time: "0:1:0", duration: "16n"},
      {note: 'A3', time: "0:1:1", duration: "16n"},
      {note: 'B3', time: "0:1:2", duration: "16n"},
      {note: 'G3', time: "0:1:3", duration: "16n"},
      {note: 'C4', time: "0:2:0", duration: "8n"},
    ],
    correctPosition: {
      beat: 6,    // Beat 6 - late timeline
      pitch: 0  // C4 octave
    }
  }
];

export const bachInvention772Level: LevelConfig = {
  id: 'bach_invention_772',
  name: 'Bach Invention No. 1',
  description: 'Reconstruct the 8-beat opening of Bach\'s Invention No. 1 in C Major',

  // Timeline Configuration
  totalBeats: 8,              // 8 beats total
  tempo: 76,                  // BPM
  key: 'C Major',             // C Major
  timeSignature: [4, 4],      // Common time

  // Fragment Configuration - converted from legacy format
  fragments: bach772Fragments,

  // Solution Hints - auto-generated for all fragment pairs
  solutionHints: generateFragmentPairHints(bach772Fragments),

  // Difficulty/Hints
  showBeatNumbers: true,
  allowPartialSolutions: false,
};

// Bach Invention No. 8 in F Major, BWV 779 - Simplified 6-beat version
const bach779Fragments: Fragment[] = [
  {
    id: 'fragment_779_1',
    name: 'Opening Phrase',
    voice: 'melody',
    beatDuration: 6.5,        // 6.5 beats
    notes: [
      {note: 'F4', time: "0:0:2", duration: "8n"},
      {note: 'A4', time: "0:1:0", duration: "8n"},
      {note: 'F4', time: "0:1:2", duration: "8n"},
      {note: 'C5', time: "0:2:0", duration: "8n"},
      {note: 'F4', time: "0:2:2", duration: "8n"},
      {note: 'F5', time: "0:3:0", duration: "8n"},
      {note: 'E5', time: "0:3:2", duration: "16n"},
      {note: 'D5', time: "0:3:3", duration: "16n"},
      {note: 'C5', time: "0:4:0", duration: "16n"},
      {note: 'D5', time: "0:4:1", duration: "16n"},
      {note: 'C5', time: "0:4:2", duration: "16n"},
      {note: 'Bb4', time: "0:4:3", duration: "16n"},
      {note: 'A4', time: "0:5:0", duration: "16n"},
      {note: 'Bb4', time: "0:5:1", duration: "16n"},
      {note: 'A4', time: "0:5:2", duration: "16n"},
      {note: 'G4', time: "0:5:3", duration: "16n"},
      {note: 'F4', time: "0:6:0", duration: "8n"},
    ],
    correctPosition: {
      beat: 0,    // Start at beginning
      pitch: 0
    }
  },
  {
    id: 'fragment_779_2',
    name: 'Middle Phrase',
    voice: 'melody',
    beatDuration: 6.5,        // 6.5 beats
    notes: [
      {note: 'F3', time: "0:0:2", duration: "8n"},
      {note: 'A3', time: "0:1:0", duration: "8n"},
      {note: 'F3', time: "0:1:2", duration: "8n"},
      {note: 'C4', time: "0:2:0", duration: "8n"},
      {note: 'F3', time: "0:2:2", duration: "8n"},
      {note: 'F4', time: "0:3:0", duration: "8n"},
      {note: 'E4', time: "0:3:2", duration: "16n"},
      {note: 'D4', time: "0:3:3", duration: "16n"},
      {note: 'C4', time: "0:4:0", duration: "16n"},
      {note: 'D4', time: "0:4:1", duration: "16n"},
      {note: 'C4', time: "0:4:2", duration: "16n"},
      {note: 'Bb3', time: "0:4:3", duration: "16n"},
      {note: 'A3', time: "0:5:0", duration: "16n"},
      {note: 'Bb3', time: "0:5:1", duration: "16n"},
      {note: 'A3', time: "0:5:2", duration: "16n"},
      {note: 'G3', time: "0:5:3", duration: "16n"},
      {note: 'F3', time: "0:6:0", duration: "8n"},
    ],
    correctPosition: {
      beat: 3,    // Beat 3
      pitch: 0
    }
  },
];

export const bachInvention779Level: LevelConfig = {
  id: 'bach_invention_779',
  name: 'Bach Invention No. 8',
  description: 'A simplified 6-beat arrangement based on Bach Invention No. 8 in F Major',

  // Timeline Configuration - shorter level
  totalBeats: 9,              // 9 beats total
  tempo: 110,                 // Moderate tempo
  key: 'F Major',             // F Major
  timeSignature: [3, 4],      // 3/4

  // Fragment Configuration
  fragments: bach779Fragments,

  // Solution Hints - auto-generated for all fragment pairs
  solutionHints: generateFragmentPairHints(bach779Fragments),

  // Difficulty/Hints - easier level
  showBeatNumbers: true,
  allowPartialSolutions: true,    // Allow partial solutions for easier difficulty
};

// Bach Invention No. 4 in D Minor, BWV 775
const bach775Fragments: Fragment[] = [
    {
      id: 'fragment_775_1',
      name: 'Opening Theme',
      voice: 'right',
      beatDuration: 12,        // 4 measures of 3/8 time
      notes: [
        // First measure (sixteenth notes)
        {note: 'D4', time: "0:0:0", duration: "16n"},
        {note: 'E4', time: "0:0:1", duration: "16n"},
        {note: 'F4', time: "0:0:2", duration: "16n"},
        {note: 'G4', time: "0:0:3", duration: "16n"},
        {note: 'A4', time: "0:1:0", duration: "16n"},
        {note: 'Bb4', time: "0:1:1", duration: "16n"},
        // Second measure (sixteenth notes)
        {note: 'C#4', time: "1:0:0", duration: "16n"},
        {note: 'Bb4', time: "1:0:1", duration: "16n"},
        {note: 'A4', time: "1:0:2", duration: "16n"},
        {note: 'G4', time: "1:0:3", duration: "16n"},
        {note: 'F4', time: "1:1:0", duration: "16n"},
        {note: 'E4', time: "1:1:1", duration: "16n"},
        // Third measure - 3 eighth notes
        {note: 'F4', time: "2:0:0", duration: "8n"},
        {note: 'A4', time: "2:0:2", duration: "8n"},
        {note: 'D5', time: "2:1:0", duration: "8n"},
        // Fourth measure - 3 eighth notes
        {note: 'G4', time: "2:1:2", duration: "8n"},
        {note: 'C#5', time: "2:2:0", duration: "8n"},
        {note: 'E5', time: "2:2:2", duration: "8n"},
      ],
      correctPosition: {
        beat: 0,
        pitch: 0
      }
    },
    {
      id: 'fragment_775_2',
      name: 'Bass Theme',
      voice: 'left',
      beatDuration: 12,        // 4 measures of 3/8 time
      notes: [
        // First measure (sixteenth notes)
        {note: 'D3', time: "0:0:0", duration: "16n"},
        {note: 'E3', time: "0:0:1", duration: "16n"},
        {note: 'F3', time: "0:0:2", duration: "16n"},
        {note: 'G3', time: "0:0:3", duration: "16n"},
        {note: 'A3', time: "0:1:0", duration: "16n"},
        {note: 'Bb3', time: "0:1:1", duration: "16n"},
        // Second measure (sixteenth notes)
        {note: 'C#3', time: "1:0:0", duration: "16n"},
        {note: 'Bb3', time: "1:0:1", duration: "16n"},
        {note: 'A3', time: "1:0:2", duration: "16n"},
        {note: 'G3', time: "1:0:3", duration: "16n"},
        {note: 'F3', time: "1:1:0", duration: "16n"},
        {note: 'E3', time: "1:1:1", duration: "16n"},
        // Third measure - 3 eighth notes
        {note: 'F3', time: "2:0:0", duration: "8n"},
        {note: 'A3', time: "2:0:2", duration: "8n"},
        {note: 'D4', time: "2:1:0", duration: "8n"},
        // Fourth measure - 3 eighth notes
        {note: 'E3', time: "2:1:2", duration: "8n"},
        {note: 'G3', time: "2:2:0", duration: "8n"},
        {note: 'C#4', time: "2:2:2", duration: "8n"},
      ],
      correctPosition: {
        beat: 6,
        pitch: 0
      }
    },
    {
      id: 'fragment_775_3',
      name: 'Upper Voice',
      voice: 'right',
      beatDuration: 16,        // 5+ measures worth of notes
      notes: [
        // First measure (sixteenth notes) - Fragment 1 up an octave
        {note: 'D5', time: "0:0:0", duration: "16n"},
        {note: 'E5', time: "0:0:1", duration: "16n"},
        {note: 'F5', time: "0:0:2", duration: "16n"},
        {note: 'G5', time: "0:0:3", duration: "16n"},
        {note: 'A5', time: "0:1:0", duration: "16n"},
        {note: 'Bb5', time: "0:1:1", duration: "16n"},
        // Second measure (sixteenth notes) - Fragment 1 up an octave
        {note: 'C#5', time: "1:0:0", duration: "16n"},
        {note: 'Bb5', time: "1:0:1", duration: "16n"},
        {note: 'A5', time: "1:0:2", duration: "16n"},
        {note: 'G5', time: "1:0:3", duration: "16n"},
        {note: 'F5', time: "1:1:0", duration: "16n"},
        {note: 'E5', time: "1:1:1", duration: "16n"},
        // Continue with new sixteenth notes - Third measure
        {note: 'F5', time: "2:0:0", duration: "16n"},
        {note: 'D5', time: "2:0:1", duration: "16n"},
        {note: 'E5', time: "2:0:2", duration: "16n"},
        {note: 'F5', time: "2:0:3", duration: "16n"},
        {note: 'G5', time: "2:1:0", duration: "16n"},
        {note: 'A5', time: "2:1:1", duration: "16n"},
        // Fourth measure
        {note: 'Bb4', time: "3:0:0", duration: "16n"},
        {note: 'A5', time: "3:0:1", duration: "16n"},
        {note: 'G5', time: "3:0:2", duration: "16n"},
        {note: 'F5', time: "3:0:3", duration: "16n"},
        {note: 'E5', time: "3:1:0", duration: "16n"},
        {note: 'D5', time: "3:1:1", duration: "16n"},
        // Fifth measure
        {note: 'E5', time: "4:0:0", duration: "16n"},
        {note: 'C5', time: "4:0:1", duration: "16n"},
        {note: 'D5', time: "4:0:2", duration: "16n"},
        {note: 'E5', time: "4:0:3", duration: "16n"},
        {note: 'F5', time: "4:1:0", duration: "16n"},
        {note: 'G5', time: "4:1:1", duration: "16n"},
        // Sixth measure
        {note: 'A4', time: "5:0:0", duration: "16n"},
        {note: 'G5', time: "5:0:1", duration: "16n"},
        {note: 'F5', time: "5:0:2", duration: "16n"},
        {note: 'E5', time: "5:0:3", duration: "16n"},
        {note: 'D5', time: "5:1:0", duration: "16n"},
        {note: 'C5', time: "5:1:1", duration: "16n"},
      ],
      correctPosition: {
        beat: 12,
        pitch: 0
      }
    },
    {
      id: 'fragment_775_4',
      name: 'Bass Line',
      voice: 'left',
      beatDuration: 12,        // 4 measures of 3/8 time (12 eighth notes)
      notes: [
        // First measure - 3 eighth notes
        {note: 'D3', time: "0:0:0", duration: "8n"},
        {note: 'D4', time: "0:0:2", duration: "8n"},
        {note: 'F3', time: "0:1:0", duration: "8n"},
        // Second measure - 3 eighth notes
        {note: 'G3', time: "1:0:0", duration: "8n"},
        {note: 'A3', time: "1:0:2", duration: "8n"},
        {note: 'Bb3', time: "1:1:0", duration: "8n"},
        // Third measure - 3 eighth notes
        {note: 'C3', time: "2:0:0", duration: "8n"},
        {note: 'C4', time: "2:0:2", duration: "8n"},
        {note: 'E3', time: "2:1:0", duration: "8n"},
        // Fourth measure - 3 eighth notes
        {note: 'F3', time: "3:0:0", duration: "8n"},
        {note: 'G3', time: "3:0:2", duration: "8n"},
        {note: 'A3', time: "3:1:0", duration: "8n"},
      ],
      correctPosition: {
        beat: 18,
        pitch: 0
      }
    }
];

export const bachInvention775Level: LevelConfig = {
  id: 'bach_invention_775',
  name: 'Bach Invention No. 4',
  description: 'Place the opening theme of Bach\'s Invention No. 4 in D Minor',

  // Timeline Configuration
  totalBeats: 24,             // 24 beats (8 measures of 3/8 time, 3 beats per measure)
  tempo: 70,                  // Tempo marking for 3/8 time
  timeSignature: [3, 8],      // 3/8 time signature
  key: 'D Minor',             // D Minor

  // Fragment Configuration
  fragments: bach775Fragments,

  // Solution hints - auto-generated for all fragment pairs
  solutionHints: generateFragmentPairHints(bach775Fragments),

  // Difficulty/Hints
  showBeatNumbers: true,
  allowPartialSolutions: false,  // Single fragment must be placed correctly
};

// Export all levels
export const allLevels: LevelConfig[] = [
  bachInvention779Level,
  bachInvention784Level,
  bachInvention772Level,
  bachInvention775Level,
];

// Utility to get level by ID
export const getLevelById = (id: string): LevelConfig | undefined => {
  return allLevels.find(level => level.id === id);
};
